import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Mail, AlertCircle, LogOut } from 'lucide-react'
import Modal from './Modal'
import { useEmailVerificationModal } from '@/stores/emailVerificationStore'
import { useAuth } from '@/stores/authStore'
import { resendVerificationEmail } from '@/services/authService'
import { toast } from '@/stores/toastStore'

export default function EmailVerificationModal() {
  const { t } = useTranslation('auth')
  const { isOpen, hideModal } = useEmailVerificationModal()
  const { user, logout } = useAuth()
  const [isResending, setIsResending] = useState(false)

  const handleResendEmail = async () => {
    setIsResending(true)
    try {
      await resendVerificationEmail()
      toast.success(t('emailVerificationRequired.resendSuccess'))
    } catch (error) {
      toast.error(t('emailVerificationRequired.resendError'))
    } finally {
      setIsResending(false)
    }
  }

  const handleLogout = () => {
    logout()
    hideModal()
    window.location.href = '/login'
  }

  // Prevent closing the modal by clicking outside or pressing escape
  const preventClose = () => {
    // Do nothing - modal cannot be closed
  }

  return (
    <Modal isOpen={isOpen} onClose={preventClose} size="md">
      <div className="text-center">
        {/* Icon */}
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Mail className="w-8 h-8 text-white" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 mb-3">
          {t('emailVerificationRequired.title')}
        </h2>

        {/* Message */}
        <p className="text-gray-600 mb-2">{t('emailVerificationRequired.message')}</p>

        {/* User Email */}
        {user?.email && (
          <p className="text-sm text-gray-700 font-medium mb-4">
            {user.email}
          </p>
        )}

        {/* Description */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700 text-left">
              {t('emailVerificationRequired.description')}
            </p>
          </div>
        </div>

        {/* Check Inbox */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <p className="text-sm font-medium text-gray-900 mb-2">
            {t('emailVerificationRequired.checkInbox')}
          </p>
          <p className="text-xs text-gray-600">
            {t('emailVerificationRequired.checkSpam')}
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          {/* Resend Email Button */}
          <button
            onClick={handleResendEmail}
            disabled={isResending}
            className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors shadow-md flex items-center justify-center gap-2"
          >
            <Mail className="w-5 h-5" />
            {isResending
              ? t('verifyEmail.sending')
              : t('emailVerificationRequired.resendButton')}
          </button>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="w-full px-4 py-3 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            <LogOut className="w-5 h-5" />
            {t('emailVerificationRequired.logoutButton')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
